import { toApiError } from './api-error.ts'
import { api } from './api.ts'

export interface ViewerAccess {
  pageIds: string[]
  unavailablePageIds: string[]
  reason?: string
}

/**
 * Read one answer from `GET /viewer-access`.
 *
 * A malformed answer cannot grant access or render a page.
 *
 * @param answer - The parsed JSON body.
 * @returns The viewer's access.
 */
export function parseViewerAccess(answer: unknown): ViewerAccess {
  if (
    typeof answer !== 'object' ||
    answer === null ||
    !('pageIds' in answer) ||
    !('unavailablePageIds' in answer)
  ) {
    throw new Error('Invalid viewer access response')
  }
  const { pageIds, unavailablePageIds } = answer
  if (
    !Array.isArray(pageIds) ||
    !pageIds.every(id => typeof id === 'string') ||
    !Array.isArray(unavailablePageIds) ||
    !unavailablePageIds.every(id => typeof id === 'string')
  ) {
    throw new Error('Invalid viewer access response')
  }
  const reason = 'reason' in answer ? answer.reason : undefined
  return typeof reason === 'string'
    ? { pageIds, unavailablePageIds, reason }
    : { pageIds, unavailablePageIds }
}

/**
 * Ask the backend which pages the signed-in employee may open.
 *
 * @returns The viewer's access.
 * @throws {ApiRequestError} when the backend refuses or does not answer.
 */
export async function fetchViewerAccess(): Promise<ViewerAccess> {
  try {
    return parseViewerAccess(await api.get('viewer-access').json())
  } catch (error) {
    throw await toApiError(error)
  }
}
